import React, { useState, useEffect, useRef } from 'react';
import { api } from '../../services/api';
import { parseIterationResponse } from '../../services/iterationResponse';

interface Props {
  strategyId?: string;
  onComplete: () => void;
}

const Module1Problem: React.FC<Props> = ({ strategyId, onComplete }) => {
  const [problemParagraph, setProblemParagraph] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [chatMessage, setChatMessage] = useState('');
  const [chatHistory, setChatHistory] = useState<{role: string, content: string}[]>([]);
  
  const [missingInfo, setMissingInfo] = useState<string[]>([]);
  const [manualInputs, setManualInputs] = useState<{[key:string]: string}>({});
  const [showInsightQuestion, setShowInsightQuestion] = useState(false);
  const [showInsightInput, setShowInsightInput] = useState(false);
  const [manualInsight, setManualInsight] = useState('');
  const [justificacionRol, setJustificacionRol] = useState('');
  const [justificacionDiagnostico, setJustificacionDiagnostico] = useState('');
  const [casoDiagnostico, setCasoDiagnostico] = useState('');
  const [fortalezasDebilidades, setFortalezasDebilidades] = useState('');
  const [historyVersions, setHistoryVersions] = useState<any[]>([]);
  const [dots, setDots] = useState('');
  const [showUndoConfirm, setShowUndoConfirm] = useState(false);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (loading) {
      interval = setInterval(() => {
        setDots(prev => prev.length >= 3 ? '' : prev + '.');
      }, 400);
    } else {
      setDots('');
    }
    return () => clearInterval(interval);
  }, [loading]);

  const isInitialMount = useRef(true);
  const isGenerating = useRef(false);
  const chatEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [chatHistory, loading]);

  useEffect(() => {
    const checkExisting = async () => {
      try {
        const res = await api.get(`/strategies/${strategyId}`);
        const data = res.data;
        
        if (data && data.module_1_problem) {
          // Ya tiene datos, cargarlos y no reprocesar
          let parsed = data.module_1_problem;
          if (typeof data.module_1_problem === 'string') {
              try { parsed = JSON.parse(data.module_1_problem); } catch(e){}
          }
          if (parsed && (parsed.parrafo || typeof parsed === 'string')) {
            setProblemParagraph(parsed.parrafo || parsed);
            if (parsed.justificacion_rol) setJustificacionRol(parsed.justificacion_rol);
            if (parsed.justificacion_diagnostico) setJustificacionDiagnostico(parsed.justificacion_diagnostico);
            if (parsed.caso) setCasoDiagnostico(parsed.caso);
            if (parsed.fortalezas_y_debilidades) setFortalezasDebilidades(parsed.fortalezas_y_debilidades);
            if (parsed.history) setHistoryVersions(parsed.history);
            return;
          }
        }
        
        // Si no hay datos validos, procesar
        generateProblem();
      } catch (err) {
        console.error("Error cargando estrategia", err);
        generateProblem();
      }
    };
    
    if (strategyId && isInitialMount.current) {
      isInitialMount.current = false;
      checkExisting();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [strategyId]);

  const generateProblem = async () => {
    if (isGenerating.current) return;
    isGenerating.current = true;
    setLoading(true);
    setMissingInfo([]);
    try {
      const res = await api.post(`/strategies/${strategyId}/modules/1/process`, { manual_inputs: manualInputs });
      const data = res.data;

      if (data.status === 'needs_info') {
        setMissingInfo(data.missing_info);
      } else if (data.module_1_problem) {
        let parsed = data.module_1_problem;
        if (typeof data.module_1_problem === 'string') {
            try { parsed = JSON.parse(data.module_1_problem); } catch(e){}
        }
        setProblemParagraph(parsed.parrafo || data.module_1_problem);
        if (parsed.justificacion_rol) setJustificacionRol(parsed.justificacion_rol);
        if (parsed.justificacion_diagnostico) setJustificacionDiagnostico(parsed.justificacion_diagnostico);
        if (parsed.caso) setCasoDiagnostico(parsed.caso);
        if (parsed.fortalezas_y_debilidades) setFortalezasDebilidades(parsed.fortalezas_y_debilidades);
      }
    } catch (err: any) {
      const message = err?.response?.data?.error || err?.message || String(err);
      alert('Error generando el Módulo 1: ' + message);
    } finally {
      setLoading(false);
      isGenerating.current = false;
    }
  };

  const sendChat = async () => {
    if (!chatMessage || loading) return;
    setLoading(true);
    isGenerating.current = true;
    setChatHistory(prev => [...prev, { role: 'user', content: chatMessage }]);
    const currentMsg = chatMessage;
    setChatMessage('');
    
    try {
      const res = await api.post(`/strategies/${strategyId}/modules/1/chat`, { message: currentMsg });
      const result = parseIterationResponse<any>(res.data);
      const state = result.state || {};

      if (typeof state.parrafo === 'string' && state.parrafo.trim() !== '') {
        setProblemParagraph(state.parrafo);
      }
      if (typeof state.justificacion_diagnostico === 'string' || typeof state.justificacion_rol === 'string') {
        setJustificacionDiagnostico(state.justificacion_diagnostico || '');
      }
      if (typeof state.justificacion_rol === 'string') {
        setJustificacionRol(state.justificacion_rol);
      }
      if (typeof state.caso === 'string') {
        setCasoDiagnostico(state.caso);
      }
      if (typeof state.fortalezas_y_debilidades === 'string') {
        setFortalezasDebilidades(state.fortalezas_y_debilidades);
      }
      if (result.history) {
        setHistoryVersions(result.history);
      } else if (Array.isArray(state.history)) {
        setHistoryVersions(state.history);
      }

      const replyContent = result.reply;
      if (replyContent && replyContent.trim() !== '' && replyContent !== '{"error":"Failed to parse state"}') {
        setChatHistory(prev => [...prev, { role: 'assistant', content: replyContent }]);
      }
    } catch (err: any) {
      console.error(err);
      const detail = err?.response?.data?.error;
      setChatHistory(prev => [...prev, {
        role: 'assistant',
        content: detail
          ? 'No pude aplicar el cambio: ' + detail
          : 'No pude aplicar el cambio. Revisa la conexión e inténtalo de nuevo.',
      }]);
    } finally {
      setLoading(false);
      isGenerating.current = false;
    }
  };

  const handleUndo = () => {
      setShowUndoConfirm(true);
  };

  const confirmUndo = async () => {
      setShowUndoConfirm(false);
      setLoading(true);
      try {
          const res = await api.post(`/strategies/${strategyId}/modules/1/undo`);
          const data = res.data;

          if (data.parrafo) setProblemParagraph(data.parrafo);
          if (data.justificacion_rol) setJustificacionRol(data.justificacion_rol);
          setJustificacionDiagnostico(data.justificacion_diagnostico || data.justificacion_rol || '');
          setCasoDiagnostico(data.caso || '');
          if (data.fortalezas_y_debilidades) setFortalezasDebilidades(data.fortalezas_y_debilidades);
          setHistoryVersions(data.history || []);
          
          setChatHistory(prev => [...prev, { role: 'assistant', content: 'He deshecho el último cambio y restaurado la versión anterior.' }]);
      } catch (err) {
          setChatHistory(prev => [...prev, { role: 'assistant', content: 'Ocurrió un error al deshacer.' }]);
      } finally {
          setLoading(false);
      }
  };

  const handleManualInputChange = (key: string, value: string) => {
      setManualInputs(prev => ({...prev, [key]: value}));
  };

  const handleOK = () => {
      setShowInsightQuestion(true);
  };

  const handleSaveInsight = async () => {
      setLoading(true);
      try {
          await api.post(`/strategies/${strategyId}/manual-insight`, { insight: manualInsight });
          handleFinalComplete();
      } catch (err: any) {
          alert('Error: ' + err.message);
      } finally {
          setLoading(false);
      }
  };

  const handleNoNeedToGenerate = async () => {
      setLoading(true);
      try {
          // Initialize Module 2 as pending AI generation (does NOT skip it).
          // The backend update-state for module 2 now only sets CurrentModule=2
          // without overwriting existing data.
          await api.put(`/strategies/${strategyId}/modules/2/update-state`, {
              needs_generation: true
          });
          handleFinalComplete();
      } catch (err: any) {
          alert('Error al avanzar al Módulo II: ' + (err.response?.data?.error || err.message));
      } finally {
          setLoading(false);
      }
  };

  const handleFinalComplete = () => {
      onComplete();
  };

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>Módulo I: El Problema</h2>
      <p style={styles.description}>Definir el problema de negocio, reto del consumidor y reto creativo en un solo párrafo integrador.</p>

      {missingInfo.length > 0 && !problemParagraph && (
          <div style={styles.missingInfoBox}>
              <h3 style={{fontSize: '16px', color: '#B45309'}}>¡Falta información en el Brief!</h3>
              <p>Los agentes no encontraron la siguiente información. Por favor proveela para continuar:</p>
              {missingInfo.map(info => (
                  <div key={info} style={{marginBottom: '12px'}}>
                      <label style={{display: 'block', fontWeight: 600, marginBottom: '4px'}}>{info}</label>
                      <input 
                          style={styles.chatInput} 
                          value={manualInputs[info] || ''} 
                          onChange={(e) => handleManualInputChange(info, e.target.value)} 
                          placeholder={`Ingresa ${info}`}
                      />
                  </div>
              ))}
              <button style={styles.primaryButton} onClick={generateProblem} disabled={loading}>
                  {loading ? 'Re-procesando...' : 'Continuar con esta info'}
              </button>
          </div>
      )}

      {!problemParagraph && missingInfo.length === 0 ? (
        loading ? (
          <div style={styles.loadingStatus}>
            <div style={styles.spinner}></div>
            <span style={{color: 'var(--accent)', fontWeight: 500}}>Generando con Agentes (ROL, RETO, CREA){dots}</span>
          </div>
        ) : (
          <button style={styles.primaryButton} onClick={generateProblem}>
            Generar Párrafo Integrador
          </button>
        )
      ) : problemParagraph && !showInsightQuestion ? (
        <>
          <div style={styles.paragraphBox}>
            <h3 style={{ fontSize: '14px', marginBottom: '8px' }}>Definición del Problema (Párrafo Integrador):</h3>
            <p style={{ margin: 0 }}>{problemParagraph}</p>
          </div>
          
          {(justificacionDiagnostico || justificacionRol) && (
              <div style={{...styles.paragraphBox, backgroundColor: 'var(--box-blue-bg)', borderColor: 'var(--box-blue-border)'}}>
                  <h3 style={{ fontSize: '14px', marginBottom: '8px', color: 'var(--box-blue-title)' }}>
                    Justificación del Diagnóstico{ casoDiagnostico ? ` (Caso ${casoDiagnostico})` : ' (Agente ROL)' }:
                  </h3>
                  <p style={{ margin: 0, fontSize: '14px', color: 'var(--box-blue-text)' }}>{justificacionDiagnostico || justificacionRol}</p>
              </div>
          )}

          {fortalezasDebilidades && (
              <div style={{...styles.paragraphBox, backgroundColor: 'var(--box-purple-bg)', borderColor: 'var(--box-purple-border)'}}>
                  <h3 style={{ fontSize: '14px', marginBottom: '8px', color: 'var(--box-purple-title)' }}>Análisis: Fortalezas y Debilidades</h3>
                  <p style={{ margin: 0, fontSize: '14px', color: 'var(--box-purple-text)' }}>{fortalezasDebilidades}</p>
              </div>
          )}

          <div style={styles.chatSection}>
            <h3 style={{ fontSize: '14px', marginBottom: '16px' }}>Iterar vía chat ("¿qué te hace ruido?")</h3>
            <div style={styles.chatHistory}>
              {chatHistory.map((msg, i) => (
                <div key={i} style={{ ...styles.chatBubble, alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start', background: msg.role === 'user' ? 'var(--bg-hover)' : 'var(--accent)', color: msg.role === 'user' ? 'var(--text-primary)' : '#FFF' }}>
                  {msg.content}
                </div>
              ))}
              {loading && (
                <div style={{ ...styles.chatBubble, alignSelf: 'flex-start', background: 'var(--accent)', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <style>
                    {`
                      @keyframes typing {
                        0%, 100% { transform: translateY(0); opacity: 0.5; }
                        50% { transform: translateY(-3px); opacity: 1; }
                      }
                      .dot {
                        width: 6px;
                        height: 6px;
                        background-color: #FFF;
                        border-radius: 50%;
                        animation: typing 1.4s infinite ease-in-out;
                      }
                      .dot:nth-child(1) { animation-delay: 0s; }
                      .dot:nth-child(2) { animation-delay: 0.2s; }
                      .dot:nth-child(3) { animation-delay: 0.4s; }
                    `}
                  </style>
                  <div className="dot" />
                  <div className="dot" />
                  <div className="dot" />
                </div>
              )}
              <div ref={chatEndRef} />
            </div>
            <div style={styles.chatInputRow}>
              <input style={styles.chatInput} value={chatMessage} onChange={e => setChatMessage(e.target.value)} placeholder="Escribe tus comentarios aquí..." onKeyDown={e => e.key === 'Enter' && !loading && sendChat()} disabled={loading} />
              <button style={styles.chatButton} onClick={sendChat} disabled={loading}>{loading ? '...' : 'Enviar'}</button>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '16px', marginTop: '24px' }}>
              {historyVersions && historyVersions.length > 0 && (
                  <button style={styles.secondaryButton} onClick={handleUndo} disabled={loading}>
                      ⟲ Deshacer
                  </button>
              )}
              <button style={styles.successButton} onClick={handleOK}>Dar el OK y Continuar</button>
          </div>
        </>
      ) : showInsightInput ? (
          <div style={styles.insightQuestionBox}>
              <h3 style={{ fontSize: '18px', marginBottom: '16px' }}>Escribe tu Insight</h3>
              <textarea 
                  style={{...styles.chatInput, minHeight: '100px', marginBottom: '16px'}} 
                  value={manualInsight} 
                  onChange={e => setManualInsight(e.target.value)} 
                  placeholder="Quiero [deseo] pero me pasa [tensión]"
              />
              <div style={{ display: 'flex', gap: '16px' }}>
                  <button style={styles.primaryButton} onClick={handleSaveInsight} disabled={loading || !manualInsight.trim()}>Guardar y Avanzar</button>
                  <button style={styles.secondaryButton} onClick={() => setShowInsightInput(false)} disabled={loading}>Atrás</button>
              </div>
          </div>
      ) : showInsightQuestion ? (
          <div style={styles.insightQuestionBox}>
              <h3 style={{ fontSize: '18px', marginBottom: '16px' }}>¿Tienes el Insight ya definido desde la arquitectura de marca?</h3>
              <div style={{ display: 'flex', gap: '16px' }}>
                  <button style={styles.primaryButton} onClick={() => setShowInsightInput(true)}>Sí, avanzar al Módulo II</button>
                  <button style={styles.secondaryButton} onClick={handleNoNeedToGenerate} disabled={loading}>No, necesito generarlo</button>
              </div>
          </div>
      ) : null}

      {showUndoConfirm && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, 
          backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 9999, 
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <div style={{
            background: 'var(--bg-card)', padding: '24px', borderRadius: '12px', 
            border: '1px solid var(--border)', maxWidth: '400px', width: '90%',
            boxShadow: '0 10px 25px rgba(0,0,0,0.5)'
          }}>
            <h3 style={{ fontSize: '18px', marginBottom: '12px', marginTop: 0, color: 'var(--text-primary)' }}>Deshacer Cambio</h3>
            <p style={{ marginBottom: '24px', fontSize: '15px', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
              ¿Estás seguro de deshacer el último cambio? Esto restaurará la versión anterior generada por la IA.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              <button 
                style={{...styles.secondaryButton, padding: '10px 20px'}} 
                onClick={() => setShowUndoConfirm(false)}>
                Cancelar
              </button>
              <button 
                style={{...styles.primaryButton, padding: '10px 20px', background: '#DC2626'}} 
                onClick={confirmUndo}>
                Sí, deshacer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: { display: 'flex', flexDirection: 'column', gap: '16px', boxSizing: 'border-box', maxWidth: '100%' },
  title: { fontSize: '24px', color: 'var(--text-primary)', marginBottom: '8px', boxSizing: 'border-box' },
  description: { color: 'var(--text-secondary)', marginBottom: '24px', boxSizing: 'border-box', wordBreak: 'break-word' },
  primaryButton: { background: 'var(--accent)', color: '#fff', padding: '12px 24px', border: 'none', borderRadius: '4px', fontWeight: 600, cursor: 'pointer', alignSelf: 'flex-start', boxSizing: 'border-box' },
  secondaryButton: { background: 'transparent', color: 'var(--text-primary)', border: '1px solid var(--text-primary)', padding: '12px 24px', borderRadius: '4px', fontWeight: 600, cursor: 'pointer', alignSelf: 'center', boxSizing: 'border-box' },
  successButton: { background: 'var(--success)', color: '#fff', padding: '12px 24px', border: 'none', borderRadius: '4px', fontWeight: 600, cursor: 'pointer', alignSelf: 'center', boxSizing: 'border-box' },
  paragraphBox: { padding: '24px', border: '1px solid var(--border)', borderRadius: '8px', background: 'var(--bg-hover)', marginBottom: '24px', color: 'var(--text-primary)', boxSizing: 'border-box', wordBreak: 'break-word', overflowWrap: 'break-word' },
  missingInfoBox: { padding: '24px', border: '1px solid var(--warning)', borderRadius: '8px', background: 'rgba(217, 119, 6, 0.1)', marginBottom: '24px', color: 'var(--warning)', boxSizing: 'border-box', wordBreak: 'break-word', overflowWrap: 'break-word' },
  insightQuestionBox: { padding: '32px', border: '1px solid var(--border)', borderRadius: '8px', background: 'var(--bg-hover)', marginTop: '24px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', color: 'var(--text-primary)', boxSizing: 'border-box', wordBreak: 'break-word', overflowWrap: 'break-word' },
  chatSection: { border: '1px solid var(--border)', borderRadius: '8px', padding: '16px', color: 'var(--text-primary)', boxSizing: 'border-box', maxWidth: '100%' },
  chatHistory: { display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '200px', overflowY: 'auto', marginBottom: '16px', boxSizing: 'border-box', maxWidth: '100%' },
  chatBubble: { padding: '12px 16px', borderRadius: '16px', maxWidth: '80%', fontSize: '14px', color: 'var(--text-primary)', boxSizing: 'border-box', wordBreak: 'break-word' },
  chatInputRow: { display: 'flex', gap: '8px', width: '100%', boxSizing: 'border-box', maxWidth: '100%' },
  chatInput: { flex: 1, padding: '10px 16px', border: '1px solid var(--border)', borderRadius: '4px', fontSize: '14px', color: 'var(--text-primary)', background: 'transparent', boxSizing: 'border-box', minWidth: 0 },
  chatButton: { background: 'var(--accent)', color: '#fff', border: 'none', padding: '0 20px', borderRadius: '4px', cursor: 'pointer', fontWeight: 600, boxSizing: 'border-box' },
  loadingStatus: { display: 'flex', alignItems: 'center', gap: '12px', width: 'fit-content', marginBottom: '24px', alignSelf: 'flex-start', boxSizing: 'border-box' },
  spinner: { width: '16px', height: '16px', border: '2px solid var(--accent)', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite', boxSizing: 'border-box' }
};

export default Module1Problem;
